import React, { useCallback, useEffect, useState } from 'react';
import { MessageCircle, RefreshCw, Send, CheckCircle2, XCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Bar = ({ value, max }) => (
  <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden mx-3">
    <div className="h-full bg-cyan-500 rounded-full" style={{ width: `${max ? Math.round((value / max) * 100) : 0}%` }} />
  </div>
);

export default function ManyChatCard() {
  const token = sessionStorage.getItem('admin_token');
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [resyncing, setResyncing] = useState(false);
  const [resyncResult, setResyncResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/manychat/contacts`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('load failed');
      setData(await res.json());
    } catch (e) { setErr(e.message); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const resync = async () => {
    setResyncing(true); setResyncResult(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/manychat/resync`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      setResyncResult(await res.json());
      load();
    } catch (e) { setResyncResult({ error: e.message }); }
    finally { setResyncing(false); }
  };

  if (err) return null;
  if (!data) return <div className="bg-white rounded-xl border border-gray-100 p-5"><RefreshCw className="w-4 h-4 text-gray-400 animate-spin" /></div>;

  const maxInterest = Math.max(1, ...data.interests.map((i) => i.count));
  const kpis = [
    [data.total, 'Synced from ManyChat'],
    [data.consented, 'Email consent'],
    [data.in_resend_general ?? data.in_resend_audience, 'Resend — General'],
    [data.in_resend_hyrox ?? 0, 'Resend — HYROX'],
  ];

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-6" data-testid="manychat-card">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <MessageCircle className="w-4 h-4 text-cyan-500" /> ManyChat Subscribers — Opt-In Interests
        </h3>
        <div className="flex items-center gap-2">
          <button onClick={resync} disabled={resyncing}
            className="text-[11px] px-2.5 py-1 rounded-md bg-cyan-50 text-cyan-700 hover:bg-cyan-100 flex items-center gap-1 disabled:opacity-50"
            data-testid="manychat-resync-btn">
            <Send className="w-3 h-3" /> {resyncing ? 'Re-pushing…' : 'Re-push to Resend'}
          </button>
          <button onClick={load} className="text-gray-400 hover:text-gray-600" data-testid="manychat-refresh">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      <p className="text-xs text-gray-500 mb-4">
        Contacts pushed by the ManyChat External Request webhook. Consented contacts with an email are auto-added to the
        Resend audiences (HYROX-interest contacts go to the race-day sequence).
      </p>
      {resyncResult && (
        <div className="mb-4 text-xs bg-cyan-50 text-cyan-800 rounded-lg p-2.5" data-testid="manychat-resync-result">
          {resyncResult.error ? `Error: ${resyncResult.error}` : `Attempted ${resyncResult.attempted} — ${JSON.stringify(resyncResult.results)}`}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        {kpis.map(([v, label]) => (
          <div key={label} className="bg-gray-50 rounded-lg p-3" data-testid={`manychat-kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
            <p className="text-lg font-bold text-gray-900">{v}</p>
            <p className="text-[11px] text-gray-500">{label}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-5">
        <div data-testid="manychat-interests">
          <p className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">By Interest (opt-in tags)</p>
          {data.interests.length === 0 ? (
            <p className="text-xs text-gray-400">No contacts synced yet — waiting on the ManyChat webhook.</p>
          ) : data.interests.map((r) => (
            <div key={r.value} className="flex items-center py-1.5 border-b border-gray-50 text-sm">
              <span className="text-gray-800 w-24 flex-shrink-0">{r.value}</span>
              <Bar value={r.count} max={maxInterest} />
              <span className="text-gray-500 tabular-nums flex-shrink-0">{r.count}</span>
            </div>
          ))}
        </div>
        <div data-testid="manychat-articles">
          <p className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">By Article / Automation</p>
          {data.interest_field_values.length === 0 && data.source_flows.length === 0 ? (
            <p className="text-xs text-gray-400">No article interest recorded yet.</p>
          ) : (
            [...data.interest_field_values, ...data.source_flows.map((f) => ({ ...f, flow: true }))].slice(0, 12).map((r) => (
              <div key={`${r.flow ? 'f' : 'a'}-${r.value}`} className="flex items-center justify-between py-1.5 border-b border-gray-50 text-sm">
                <span className="text-gray-800 truncate">{r.value}{r.flow && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded">flow</span>}</span>
                <span className="text-gray-500 tabular-nums flex-shrink-0">{r.count}</span>
              </div>
            ))
          )}
        </div>
      </div>

      {data.recent.length > 0 && (
        <div data-testid="manychat-recent">
          <p className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">Recently Synced</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] text-gray-400 uppercase">
                  <th className="py-1 pr-3 font-medium">Name</th>
                  <th className="py-1 pr-3 font-medium">Email</th>
                  <th className="py-1 pr-3 font-medium">Interests</th>
                  <th className="py-1 pr-3 font-medium">Consent</th>
                  <th className="py-1 font-medium">Resend</th>
                </tr>
              </thead>
              <tbody>
                {data.recent.slice(0, 10).map((c, i) => (
                  <tr key={i} className="border-t border-gray-50">
                    <td className="py-1.5 pr-3 text-gray-800">{c.first_name || c.name || c.ig_username || '—'}</td>
                    <td className="py-1.5 pr-3 text-gray-600">{c.email || '—'}</td>
                    <td className="py-1.5 pr-3 text-gray-600">{(c.interests || []).join(', ') || c.interest_field || '—'}</td>
                    <td className="py-1.5 pr-3">{c.consent ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> : <XCircle className="w-3.5 h-3.5 text-gray-300" />}</td>
                    <td className="py-1.5 text-gray-600">{c.resend?.synced ? (c.resend.audience === 'hyrox' ? 'HYROX' : 'General') : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
